import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { CameraAngle, Game, Video, VideoVariantEnum } from '../domain/game';
import { League } from '../domain/league';
import { VideoDownloadDialogComponent } from '../video-download-dialog/video-download-dialog.component';
import { VideoUploadDialogComponent } from '../video-upload-dialog/video-upload-dialog.component';
import { AlertService } from './alert.service';
import { GameService, VideoDownloadOptions } from './game.service';

@Injectable({
  providedIn: 'root'
})
export class GameVideoDetailsService {
  constructor(
    private dialog: MatDialog,
    private gameService: GameService,
    private alertService: AlertService
  ) {}

  ensureVideos(game: Game): void {
    if ((game?.videos ?? []).length === 0) {
      game.videos = [];
    }
  }

  addVideo(game: Game, index: number): Video {
    this.ensureVideos(game);
    const video: Video = {
      id: self.crypto.randomUUID(),
      cameraAngle: null,
      url: '',
      offset: 0
    };
    game.videos.splice(index, 0, video);
    return video;
  }

  removeVideo(game: Game, video: Video): void {
    this.ensureVideos(game);
    const index = game.videos.indexOf(video);
    if (index >= 0) {
      game.videos.splice(index, 1);
    }
  }

  addLiveStream(game: Game, liveStream: Video): void {
    this.ensureVideos(game);
    const calculatedOffset = this.calculateOffsetFromMain(game, liveStream);
    liveStream.offset = calculatedOffset ?? 0;
    game.videos.push(liveStream);
  }

  calculateOffsetFromMain(game: Game, video: Video): number | null {
    const existingMain = (game.videos ?? []).find(
      (v) => v.streamId === 'main' || v.cameraAngle === 'main'
    );
    if (!existingMain?.startTime || !video.startTime) {
      return null;
    }
    return Math.round((video.startTime - existingMain.startTime) / 1000);
  }

  updateVideoFormatFromUrl(video: Video): void {
    const url = (video.url ?? '').toLowerCase();

    if (url.endsWith('.mp4')) {
      video.format = 'mp4';
    } else if (url.endsWith('.m3u8')) {
      video.format = 'hls';
    } else if (url.endsWith('.mpd')) {
      video.format = 'dash';
    } else if (url.endsWith('.webm') || url.endsWith('.mkv')) {
      video.format = 'webm';
    }
  }

  applyUploadResult(
    game: Game,
    uploadedVideo: Pick<Video, 'url' | 'format' | 'cameraAngle' | 'variant'>,
    video?: Video
  ): Video {
    let targetVideo = video;
    if (!targetVideo) {
      targetVideo = this.addVideo(game, 0);
    }

    targetVideo.url = uploadedVideo.url;
    if (!targetVideo.format) {
      targetVideo.format = uploadedVideo.format;
    }
    if (!targetVideo.cameraAngle) {
      targetVideo.cameraAngle = uploadedVideo.cameraAngle;
    }
    if (!targetVideo.variant) {
      targetVideo.variant = uploadedVideo.variant;
    }
    return targetVideo;
  }

  findLeagueForGame(game: Game, leagues: League[]): League | null {
    return leagues.find((league) => league.sihfId === game?.league) ?? null;
  }

  async uploadVideo(
    game: Game,
    leagues: League[],
    video?: Video
  ): Promise<boolean> {
    const league = this.findLeagueForGame(game, leagues);
    if (!league) {
      return false;
    }

    const result = await firstValueFrom(
      this.dialog
        .open(VideoUploadDialogComponent, {
          width: '900px',
          data: {
            game,
            league,
            cameraAngle: video?.cameraAngle ?? CameraAngle.MAIN,
            variant: video?.variant ?? VideoVariantEnum.LIVERECORDING
          }
        })
        .afterClosed()
    );
    if (!result) {
      return false;
    }

    this.applyUploadResult(game, result, video);
    return true;
  }

  async downloadVideo(game: Game, video: Video): Promise<boolean> {
    if (!game?._id) {
      return false;
    }

    const downloadOptions = await firstValueFrom(
      this.dialog
        .open<
          VideoDownloadDialogComponent,
          { game: Game; video: Video },
          VideoDownloadOptions
        >(VideoDownloadDialogComponent, {
          width: '900px',
          data: { game, video }
        })
        .afterClosed()
    );
    if (!downloadOptions) {
      return false;
    }

    try {
      const res = await firstValueFrom(
        this.gameService.downloadVideo(game._id, video, downloadOptions)
      );
      video.downloadStatus = res.status as any;
      this.alertService.showInfo('Archive video started');
      return true;
    } catch (error: any) {
      video.downloadStatus = 'error';
      video.downloadError = error?.message;
      this.alertService.showError(
        'Could not download video: ' + (error?.message ?? 'unknown error')
      );
      return false;
    }
  }

  validateVideos(game: Game): void {
    for (const video of game.videos ?? []) {
      if (video.url?.includes(' ')) {
        throw new Error(`Invalid URL: "${video.url}" contains whitespace.`);
      }
    }
  }
}
